import { getLocalCart, setLocalCart } from '@/redux/cart/cart.local';
import { getCartApi, validateGuestCartApi } from '@/services/cart.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Enhanced cart hook with validation support for both logged and guest users
 */
export function useCart() {
  const loggedIn = useSelector((state) => state.auth.loggedIn);

  // Use different query keys for logged vs guest users
  const queryKey = loggedIn ? ['cart'] : ['cart', 'guest'];

  const cartQuery = useQuery({
    queryKey,
    queryFn: async () => {
      // Development logging (only in dev mode)
      if (process.env.NODE_ENV === 'development') {
        console.log('🛒 useCart: Fetching cart data, loggedIn:', loggedIn);
      }

      if (loggedIn) {
        try {
          const cartResponse = await getCartApi();

          if (process.env.NODE_ENV === 'development') {
            console.log('🛒 useCart: Server response received');
          }

          // Handle both possible formats of cart response
          let cartItems = [];
          let validation = null;
          let cleanup = null;

          if (cartResponse && cartResponse.items && Array.isArray(cartResponse.items)) {
            // Server returned an object with items array + validation/cleanup
            cartItems = cartResponse.items;
            validation = cartResponse.validation || null;
            cleanup = cartResponse.cleanup || null;
          } else if (Array.isArray(cartResponse)) {
            // Server returned an array directly (legacy format)
            cartItems = cartResponse;
          } else if (cartResponse === null || cartResponse === undefined) {
            // Server returned null/undefined - empty cart
            cartItems = [];
          } else {
            console.warn('🛒 useCart: Unexpected cart format:', cartResponse);
            cartItems = [];
          }

          // Save to local storage for offline access (only if valid data)
          if (Array.isArray(cartItems)) {
            setLocalCart(cartItems);
          }

          // Return consistent format
          const result = {
            items: cartItems,
            validation,
            cleanup,
            // Computed properties for easy access
            totalItems: cartItems.length,
            totalQuantity: cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
            isEmpty: cartItems.length === 0,
            hasValidationIssues: validation && !validation.is_valid,
            hasCleanupItems: cleanup && cleanup.removed_count > 0,
          };

          if (process.env.NODE_ENV === 'development') {
            console.log('🛒 useCart: Returning result:', {
              itemsCount: cartItems.length,
              totalQuantity: result.totalQuantity,
              hasValidation: !!validation,
              hasCleanup: !!cleanup,
            });
          }

          return result;
        } catch (error) {
          console.error('🛒 useCart: API error:', error);

          // Enhanced fallback to local cart with error context
          const localCart = getLocalCart();
          return {
            items: localCart,
            validation: null,
            cleanup: null,
            totalItems: localCart.length,
            totalQuantity: localCart.reduce((sum, item) => sum + (item.quantity || 1), 0),
            isEmpty: localCart.length === 0,
            hasValidationIssues: false,
            hasCleanupItems: false,
            hasError: true,
            errorMessage: error.message || 'Failed to load cart from server',
          };
        }
      }

      // For non-logged in users, get local cart and validate if has items
      const localCart = getLocalCart();

      if (process.env.NODE_ENV === 'development') {
        console.log('🛒 useCart: Guest user, localStorage items:', localCart.length);
      }

      // If guest has cart items, validate them with server
      let validation = null;
      if (localCart.length > 0) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log('🛒 useCart: Validating guest cart with', localCart.length, 'items');
          }

          const validationResult = await validateGuestCartApi(localCart);
          validation = validationResult.validation || null;

          if (process.env.NODE_ENV === 'development') {
            console.log('🛒 Guest cart validation result:', validation);
          }
        } catch (error) {
          console.warn('🛒 Guest cart validation failed:', error);

          // Log the error details for debugging
          if (error.response?.status === 422) {
            console.warn('🛒 422 Error - Check payload format:', {
              cartItems: localCart.length,
              sampleItem: localCart[0],
            });
          }

          // Continue without validation data
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('🛒 useCart: No items to validate for guest user');
        }
      }

      return {
        items: localCart,
        validation,
        cleanup: null,
        totalItems: localCart.length,
        totalQuantity: localCart.reduce((sum, item) => sum + (item.quantity || 1), 0),
        isEmpty: localCart.length === 0,
        hasValidationIssues: validation && !validation.is_valid,
        hasCleanupItems: false,
        isGuest: true,
      };
    },
    initialData: () => {
      // For non-logged users, provide immediate data from localStorage
      if (!loggedIn) {
        const localCart = getLocalCart();
        return {
          items: localCart,
          validation: null,
          cleanup: null,
          totalItems: localCart.length,
          totalQuantity: localCart.reduce((sum, item) => sum + (item.quantity || 1), 0),
          isEmpty: localCart.length === 0,
          hasValidationIssues: false,
          hasCleanupItems: false,
          isGuest: true,
        };
      }
      return undefined;
    },
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: true,
  });

  // Memoize computed values to prevent unnecessary re-renders
  const cartData = cartQuery.data;

  // Enhanced refresh function that handles both logged and guest users
  const refreshCart = useCallback(async () => {
    try {
      await cartQuery.refetch();
    } catch (error) {
      console.error('🛒 Failed to refresh cart:', error);
    }
  }, [cartQuery]);

  // Force invalidate cart cache
  const invalidateCart = useCallback(() => {
    cartQuery.queryClient?.invalidateQueries(['cart']);
  }, [cartQuery.queryClient]);

  return {
    // Main data
    data: cartData,

    // Loading states
    isLoading: cartQuery.isLoading,
    isFetching: cartQuery.isFetching,
    isError: cartQuery.isError,
    error: cartQuery.error,

    // Utility functions
    refetch: cartQuery.refetch,
    refreshCart,
    invalidateCart,

    // Computed values (memoized)
    cartItems: cartData?.items || [],
    totalItems: cartData?.totalItems || 0,
    totalQuantity: cartData?.totalQuantity || 0,
    isEmpty: cartData?.isEmpty ?? true,
    hasValidationIssues: cartData?.hasValidationIssues || false,
    hasCleanupItems: cartData?.hasCleanupItems || false,
    validation: cartData?.validation || null,
    cleanup: cartData?.cleanup || null,
  };
}
